import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { getBaseUrl } from './ApiContext';
import LoadingIndicator from './LoadingIndicator';
import './LPCommitments.css';

const baseUrl = getBaseUrl();

// Filtering happens in Postgres, not here. The median and p25 cheque are
// percentile_cont aggregates, so recomputing them in JS would be both
// slower over ~2,000 rows and a second place for the maths to drift away
// from the operator dashboard. The cost is a request per filter change,
// which is what DEBOUNCE_MS is for.
const DEBOUNCE_MS = 300;

const EMPTY_FILTERS = {
    min_check: '',
    max_check: '',
    strategy: '',
    vintage_from: '',
    vintage_to: '',
    allocator: '',
    q: '',
};

function usd(v) {
    if (v === null || v === undefined) return '—';
    const n = Math.abs(v);
    if (n >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
    if (n >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `$${Math.round(v / 1e3)}K`;
    return `$${Math.round(v)}`;
}

// Non-USD commitments keep their own symbol and are deliberately absent
// from every size aggregate, because we have no FX rate we would trust for
// a historical close. The badge exists so a euro figure in this column is
// never silently read as dollars.
const SYMBOLS = { USD: '$', EUR: '€', GBP: '£' };

function amount(v, currency) {
    if (v === null || v === undefined) return '—';
    const sym = SYMBOLS[currency] || '';
    if (Math.abs(v) >= 1e9) return `${sym}${(v / 1e9).toFixed(2)}B`;
    return `${sym}${(v / 1e6).toFixed(1)}M`;
}

function num(v, suffix) {
    return v === null || v === undefined ? '—' : `${v.toFixed(1)}${suffix}`;
}

function label(s) {
    return s ? s.replace(/_/g, ' ') : '';
}

function LPCommitments() {
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const seq = useRef(0);

    useEffect(() => {
        const handle = setTimeout(() => {
            const mine = ++seq.current;
            setLoading(true);
            const params = Object.fromEntries(
                Object.entries(filters).filter(([, v]) => v !== '')
            );
            axios.get(`${baseUrl}/api/lp-commitments`, { params })
                .then((res) => {
                    // Ignore a slow response that a newer request has
                    // already superseded, or the table flickers back to
                    // stale rows after the user has typed on.
                    if (mine !== seq.current) return;
                    setData(res.data);
                    setError(null);
                    setLoading(false);
                })
                .catch((err) => {
                    if (mine !== seq.current) return;
                    setError(err.response?.data?.error || err.message);
                    setLoading(false);
                });
        }, DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [filters]);

    const set = (key) => (e) => setFilters({ ...filters, [key]: e.target.value });

    if (error && !data) {
        return (
            <div className="lpc">
                <div className="lpc-empty">Could not load LP commitments: {error}</div>
            </div>
        );
    }
    if (!data) {
        return <div className="lpc"><LoadingIndicator /></div>;
    }

    const { meta, filters: opts, allocators, managers, funds, matching, limit } = data;

    return (
        <div className="lpc">
            <div className="lpc-header">
                <div className="lpc-title">
                    <h1>LP Commitments</h1>
                    <p>
                        Every private fund commitment five US public pension systems publish,
                        scraped from their own filings
                        {meta.newest ? ` · newest data ${meta.newest}` : ''}
                    </p>
                </div>
                <div className="lpc-stats">
                    <div className="lpc-stat">
                        <div className="n">{meta.allocators}</div>
                        <div className="l">Allocators</div>
                    </div>
                    <div className="lpc-stat">
                        <div className="n">{meta.commitments.toLocaleString()}</div>
                        <div className="l">Commitments</div>
                    </div>
                    <div className="lpc-stat">
                        <div className="n">{usd(meta.total_usd)}</div>
                        <div className="l">Total committed</div>
                    </div>
                    <div className="lpc-stat">
                        <div className="n">{meta.families.toLocaleString()}</div>
                        <div className="l">Fund families</div>
                    </div>
                </div>
            </div>

            {/* The page title invites exactly the wrong reading, so this is
                not decorative. No public record anywhere names an individual
                as an LP in a named fund. */}
            <div className="lpc-scope">
                <b>Institutions only.</b> No public record names an individual as an LP in a
                named fund, so this is peer-fund mapping and benchmarking, not a prospect
                list. Nothing here is modelled: a blank cell means the source did not publish
                that figure
                {meta.non_usd
                    ? `, and ${meta.non_usd} commitments reported in a non-USD currency are excluded from size filters and medians`
                    : ''}.
            </div>

            <div className="lpc-filters">
                <label>
                    Min cheque ($M)
                    <input value={filters.min_check} onChange={set('min_check')} placeholder="5" />
                </label>
                <label>
                    Max cheque ($M)
                    <input value={filters.max_check} onChange={set('max_check')} placeholder="50" />
                </label>
                <label>
                    Strategy
                    <select value={filters.strategy} onChange={set('strategy')}>
                        <option value="">any</option>
                        {opts.strategies.map((s) => (
                            <option key={s} value={s}>{label(s)}</option>
                        ))}
                    </select>
                </label>
                <label>
                    Vintage from
                    <input value={filters.vintage_from} onChange={set('vintage_from')} placeholder="2018" />
                </label>
                <label>
                    Vintage to
                    <input value={filters.vintage_to} onChange={set('vintage_to')} placeholder="2026" />
                </label>
                <label>
                    Allocator
                    <select value={filters.allocator} onChange={set('allocator')}>
                        <option value="">any</option>
                        {opts.allocators.map((a) => (
                            <option key={a.slug} value={a.slug}>{a.name}</option>
                        ))}
                    </select>
                </label>
                <label className="grow">
                    Fund or manager
                    <input value={filters.q} onChange={set('q')} placeholder="ventures" />
                </label>
                <button
                    type="button"
                    className="lpc-clear"
                    onClick={() => setFilters(EMPTY_FILTERS)}
                >
                    Clear
                </button>
            </div>

            <div className="lpc-matching">
                <b>{matching.toLocaleString()}</b> commitments match
                {loading && <span className="lpc-loading"> · updating</span>}
            </div>

            <div className="lpc-body">
                <h2>Allocators by cheque size</h2>
                <p className="lpc-note">
                    Ranked by median commitment within the filter. If you are raising a first
                    or second fund, p25 and the smallest cheque matter more than the median:
                    they are the sizes this LP has actually written at the bottom of its range.
                </p>
                <div className="lpc-scroll">
                    <table>
                        <thead>
                            <tr>
                                <th>Allocator</th><th className="n">Funds</th>
                                <th className="n">Median</th><th className="n">p25</th>
                                <th className="n">Smallest</th><th className="n">Largest</th>
                                <th className="n">Total</th><th className="n">Latest vintage</th>
                                <th>As of</th>
                            </tr>
                        </thead>
                        <tbody>
                            {allocators.length === 0 && (
                                <tr><td colSpan={9} className="lpc-empty">No commitments match this filter</td></tr>
                            )}
                            {allocators.map((a) => (
                                <tr key={a.slug}>
                                    <td>
                                        {a.home_url
                                            ? <a href={a.home_url} target="_blank" rel="noopener noreferrer">{a.name}</a>
                                            : a.name}
                                        <div className="sub">{a.state} {label(a.kind)}</div>
                                    </td>
                                    <td className="n">{a.n_funds.toLocaleString()}</td>
                                    <td className="n b">{usd(a.median_check)}</td>
                                    <td className="n">{usd(a.p25_check)}</td>
                                    <td className="n">{usd(a.min_check)}</td>
                                    <td className="n">{usd(a.max_check)}</td>
                                    <td className="n">{usd(a.total_usd)}</td>
                                    <td className="n">{a.latest_vintage || '—'}</td>
                                    <td className="sub">{a.as_of || ''}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <h2>Managers backed by more than one of these allocators</h2>
                <p className="lpc-note">
                    Grouped by fund family. A family with several allocators behind it and a
                    low minimum cheque is the closest this data comes to a target list: those
                    LPs demonstrably back managers in that category at that size.
                </p>
                <div className="lpc-scroll">
                    <table>
                        <thead>
                            <tr>
                                <th>Fund family</th><th className="n">Allocators</th>
                                <th className="n">Funds</th><th className="n">Median</th>
                                <th className="n">Smallest</th><th className="n">Total</th>
                                <th className="n">Vintages</th><th>Who</th>
                            </tr>
                        </thead>
                        <tbody>
                            {managers.length === 0 && (
                                <tr><td colSpan={8} className="lpc-empty">Nothing matches this filter</td></tr>
                            )}
                            {managers.map((m) => (
                                <tr key={m.fund_family}>
                                    <td>{m.fund_family}</td>
                                    <td className="n b">{m.n_allocators}</td>
                                    <td className="n">{m.n_funds}</td>
                                    <td className="n">{usd(m.median_check)}</td>
                                    <td className="n">{usd(m.min_check)}</td>
                                    <td className="n">{usd(m.total_usd)}</td>
                                    <td className="n sub">{m.first_vintage || ''}–{m.last_vintage || ''}</td>
                                    <td className="sub">{m.allocators}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <h2>
                    Commitments
                    <span className="lpc-count">
                        {matching > limit
                            ? ` — largest ${limit} of ${matching.toLocaleString()}`
                            : ` — ${matching.toLocaleString()}`}
                    </span>
                </h2>
                <div className="lpc-scroll">
                    <table>
                        <thead>
                            <tr>
                                <th>Fund</th><th>Manager</th><th>Strategy</th>
                                <th className="n">Vintage</th><th className="n">Committed</th>
                                <th className="n">Net IRR</th><th className="n">Multiple</th>
                                <th>Source</th>
                            </tr>
                        </thead>
                        <tbody>
                            {funds.length === 0 && (
                                <tr><td colSpan={8} className="lpc-empty">No commitments match this filter</td></tr>
                            )}
                            {funds.map((f, i) => (
                                <tr key={`${f.allocator}-${f.fund_name}-${i}`}>
                                    <td>
                                        {f.fund_name}
                                        {f.note && <div className="sub">{f.note}</div>}
                                    </td>
                                    <td>{f.manager_name || ''}</td>
                                    {/* Strategy is only authoritative when the
                                        allocator labelled it. Measured against
                                        WSIB's 465 declared rows, the name guess
                                        is 84% precise, so it is marked rather
                                        than presented as fact. */}
                                    <td className="sub">
                                        {label(f.strategy)}
                                        {f.source_strategy && <div className="sub2">{f.source_strategy}</div>}
                                        {f.strategy_confidence === 'inferred' && f.strategy !== 'unknown'
                                            && <div className="sub2">guessed from name</div>}
                                        {f.strategy_confidence === 'propagated'
                                            && <div className="sub2">from same manager elsewhere</div>}
                                    </td>
                                    <td className="n">{f.vintage || '—'}</td>
                                    <td className="n b">
                                        {amount(f.committed, f.currency)}
                                        {f.currency !== 'USD' && <span className="fx">not USD</span>}
                                    </td>
                                    <td className="n">{num(f.irr_pct, '%')}</td>
                                    <td className="n">{num(f.multiple, 'x')}</td>
                                    <td className="sub">
                                        {f.source_url
                                            ? (
                                                <a href={f.source_url} target="_blank" rel="noopener noreferrer">
                                                    {f.allocator} <FaExternalLinkAlt size={9} />
                                                </a>
                                            )
                                            : f.allocator}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default LPCommitments;
